/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { getAxiosForClient } from '@/lib/axios';
import { type MutationFunctionContext, useMutation } from '@tanstack/react-query';
import type {
  UpdatePreferencesRequestV2,
  UpdatePreferencesResponse,
} from '../../account/interface';
import type { AxiosResponse } from 'axios';

const URL = `${requestBaseURL}/ecomm/b2c/v2/currentUser/updatePreferences`;

type UpdatePreferencesMutationProps = {
  onSuccess?: (
    data: UpdatePreferencesResponse,
    variables: UpdatePreferencesRequestV2,
    onMutateResult: void,
    context: MutationFunctionContext,
  ) => void;
  onError?: (
    error: unknown,
    variables: UpdatePreferencesRequestV2,
    onMutateResult: void | undefined,
    context: MutationFunctionContext,
  ) => void;
  onMutate?: (variables: UpdatePreferencesRequestV2, context: MutationFunctionContext) => void;
};

const selector = (response: AxiosResponse<UpdatePreferencesResponse>): UpdatePreferencesResponse =>
  response.data;

const updatePreferences = async (values: UpdatePreferencesRequestV2) => {
  const { emailOptins, telephoneOptins } = values;

  const body: UpdatePreferencesRequestV2 = {
    emailOptins: emailOptins ?? [],
    telephoneOptins: telephoneOptins ?? [],
  };

  const response = await getAxiosForClient().put<UpdatePreferencesResponse>(URL, body);

  return selector(response);
};

export const useUpdatePreferencesV2 = (props?: UpdatePreferencesMutationProps) => {
  const { onSuccess, onError, onMutate } = props ?? {};
  return useMutation({
    mutationFn: updatePreferences,
    onSuccess,
    onMutate,
    onError,
  });
};
